import type {GridColDef, GridFilterModel} from '@mui/x-data-grid';
import type {Feature, FeatureRef} from '@src/types/featureCollections.ts';
import {
  createReference,
  parseReference,
} from '@apps-common/geocorn-sdk/api/util';
import {getGridStringOperators} from '@mui/x-data-grid-premium';
import {createHash} from 'crypto';

// In these utilities, we often just look at the last 7 chars to extract the last hashid
const HASHID_LENGTH = 7;

export const EMPTY_COLLECTION_ACTIONS_MESSAGE =
  'Upload features to enable collection view actions';

// --------------------------------------------------------
// Hash manipulation

export const getHashFromId = (id?: string) => {
  if (!id) {
    return '';
  }

  return id.slice(-HASHID_LENGTH);
};

export const hashStringSHA256 = (message?: string) => {
  if (!message) {
    return;
  }

  return createHash('sha256').update(message).digest('hex');
};

// --------------------------------------------------------
// Reference comparison

export const refEquals = (ref1: FeatureRef, ref2: FeatureRef) => {
  if (!ref1 || !ref2) {
    return false;
  }

  const parsedRef1 = parseReference(ref1);
  const parsedRef2 = parseReference(ref2);

  if (
    (parsedRef1.featureId && !parsedRef2.featureId) ||
    (!parsedRef1.featureId && parsedRef2.featureId)
  ) {
    return false;
  }

  return (
    getHashFromId(parsedRef1.featureId) === getHashFromId(parsedRef2.featureId)
  );
};

// --------------------------------------------------------
// Reference creation

export const createFullReference = (
  featureHash: string,
  collectionRef: FeatureRef,
) => {
  if (!featureHash || !collectionRef) {
    return undefined;
  }

  const {dataset, collectionId} = parseReference(collectionRef);
  if (!dataset || !collectionId) {
    return undefined;
  }

  return createReference(dataset, collectionId, featureHash);
};

export const createCollectionReference = (featureRef: FeatureRef) => {
  if (!featureRef) {
    return undefined;
  }

  const {dataset, collectionId} = parseReference(featureRef);
  if (!dataset || !collectionId) {
    return undefined;
  }

  // TODO: update createReference in geocorn-sdk to handle refs
  // without features, so we don't have to do the replace here.
  return createReference(dataset, collectionId).replace(/\/undefined$/, '');
};

// --------------------------------------------------------
// Get column definition (for use in filtering)

export const getColumns = (properties: string[]) => {
  type ValidRow = Feature;
  type ColumnWithValidField = {
    field: keyof ValidRow | string;
  };

  const columns = [
    {
      field: 'hashid',
      headerName: 'Reference',
      valueGetter: (_value: unknown, row: ValidRow) =>
        row.properties?.['pl:ref'],
      filterOperators: getGridStringOperators().filter(operator =>
        ['equals'].includes(operator.value),
      ),
    },
    // Add new columns based on unique properties
    ...(properties ?? []).map((prop: string) => ({
      field: prop,
      headerName: prop,
      valueGetter: (_value: unknown, row: ValidRow) => row.properties?.[prop],
      filterOperators: getGridStringOperators().filter(operator =>
        ['contains'].includes(operator.value),
      ),
    })),
  ] satisfies Array<GridColDef<ValidRow>> | ColumnWithValidField[];

  return columns;
};

// --------------------------------------------------------
// Transform the relevant filters for use in the API call

type GetApiFiltersParams = {
  filterModel: GridFilterModel;
  referenceFieldName: string;
};

export const getApiFilters = ({
  filterModel,
  referenceFieldName,
}: GetApiFiltersParams) =>
  filterModel.items.reduce((filters, item) => {
    const value =
      item.field === referenceFieldName
        ? (getHashFromId(item.value) ?? '')
        : item.value;

    return {
      ...filters,
      ...(value
        ? {[item.field]: item.operator === 'contains' ? `~${value}` : value}
        : {}),
    };
  }, {});
